import { clearStorage, getStorage, setStorage } from '@getjust/commons';
import { QueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import { z } from 'zod';

import type { Cart } from '@getjust/gateway';

import { cartIdAtom } from '$src/business/atoms/cart';
import { CART_QUERY_KEY } from '$src/constants';

export const CART_CACHE_SCHEMA_VERSION = 1;

export const ZCachedCart = z.object({
  schemaVersion: z.literal(CART_CACHE_SCHEMA_VERSION),
  cartId: z.string(),
  // Intentionally `unknown` (not `ZCart`): importing the runtime ZCart
  // schema from @getjust/gateway pulls backend packages into the SSR
  // bundle (breaks `Can't resolve 'fs'`). Schema-drift protection lives
  // in the schemaVersion literal above — bump it when Cart's shape
  // changes intentionally.
  cart: z.unknown(),
  savedAt: z.number(),
});

export type CachedCart = Omit<z.infer<typeof ZCachedCart>, 'cart'> & { cart: Cart };

const cacheKey = (shopId: string) => `just_cart_${shopId}`;

export function readCachedCart(shopId: string): CachedCart | null {
  if (!shopId) return null;
  const raw = getStorage<unknown>('localStorage', cacheKey(shopId));
  if (!raw) return null;
  const parsed = ZCachedCart.safeParse(raw);
  if (!parsed.success) {
    // Schema drift, corruption, or pre-version-bump entry — drop and cold-path.
    clearStorage('localStorage', cacheKey(shopId));
    return null;
  }
  return parsed.data as CachedCart;
}

export function writeCachedCart(shopId: string, cart: Cart): void {
  if (!shopId || !cart?.id) return;
  const payload: CachedCart = {
    schemaVersion: CART_CACHE_SCHEMA_VERSION,
    cartId: cart.id,
    cart,
    savedAt: Date.now(),
  };
  setStorage('localStorage', cacheKey(shopId), payload);
}

export function clearCachedCart(shopId: string): void {
  if (!shopId) return;
  clearStorage('localStorage', cacheKey(shopId));
}

/**
 * Synchronously hydrate the TanStack Query cache + Jotai cartIdAtom from
 * localStorage. Called once from _app.tsx inside the QueryClient lazy
 * initializer, BEFORE any child mounts a useCart subscription.
 *
 * Skips hydration if:
 * - we're server-side (no window),
 * - shopId is missing,
 * - no cache entry exists or Zod fails,
 * - the URL's ?data= carries a different cart.id than the cached one
 *   (merchant intentionally pointed at a different cart).
 */
export function hydrateCartCacheIntoQueryClient(
  queryClient: QueryClient,
  shopId: string | undefined,
): void {
  if (typeof window === 'undefined') return;
  if (!shopId) return;

  const cached = readCachedCart(shopId);
  if (!cached) return;

  // URL-cart-id mismatch guard. The `?data=` blob from the merchant page
  // may contain its own cart.id; if it disagrees with our cache the
  // merchant wants a different cart on this open — discard the cache.
  const urlCartId = readCartIdFromUrl();
  if (urlCartId && urlCartId !== cached.cartId) {
    clearCachedCart(shopId);
    return;
  }

  const store = getDefaultStore();
  store.set(cartIdAtom, cached.cartId);
  queryClient.setQueryData([CART_QUERY_KEY, cached.cartId], cached.cart);
  // Mark stale so the first subscriber refetches in the background.
  queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY, cached.cartId] });
}

/**
 * Best-effort sync read of the URL's ?data=<base64> blob to extract the
 * cart.id without waiting for next/router (which is async). Returns
 * undefined on any failure — caller treats undefined as "no guard,
 * proceed with cache".
 */
function readCartIdFromUrl(): string | undefined {
  try {
    const search = new URLSearchParams(window.location.search);
    const data = search.get('data');
    if (!data) return undefined;
    const decoded = atob(data);
    const parsed = JSON.parse(decoded);
    return typeof parsed?.cart?.id === 'string' ? parsed.cart.id : undefined;
  } catch {
    return undefined;
  }
}
